import { theme } from 'antd';
import { settingFieldType } from 'config/settingFieldType';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { deleteFiles } from 'library/firebase/fb.auth.utils';
import _ from 'lodash';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectDefaultGroupSettings, selectGroupSettingsGeneralID } from 'reduxStore/defaultSettings/selector';
import { selectCurrentProfileOrgSettings, selectCurrentProfilePersonSettings } from 'reduxStore/settings/selectors';
import { Button, Col, Flex, Row, Typography } from 'zui/Antd';
import DeleteOrganisationSetting from '../DeleteOrganisationSetting';
import RenderFieldTypeSetting from '../RenderFieldTypeSetting';
import RenderHeaderProfileSetting from '../RenderHeaderProfileSetting';
import RenderHeaderSetting from '../RenderHeaderSetting';
import GeneralSetting from '../GeneralSetting';
import { EditIcon, EllipsisHorizontalIcon } from 'assets/images/svg';
import ChangePasswordModal from 'zComponents/molecules/ChangePasswordModal';
import { upsertSettingsByEntityType } from 'reduxStore/settings/saga';

const { Title, Text } = Typography;

const RenderContentSetting = ({ settingGroupID, entityType }) => {
	//Hooks
	const {
		token: { marginMD, colorTextLabel, colorTextDescription, paddingXXS }
	} = theme.useToken();

	const dispatch = useDispatch();

	const [openPasswordModal, setOpenPasswordModal] = useState(false);

	// Redux state
	const personSettings = useSelector(selectCurrentProfilePersonSettings);
	const orgSettings = useSelector(selectCurrentProfileOrgSettings);
	const groupSettingsData = useSelector(selectDefaultGroupSettings);
	const personID = useSelector((state) => state.Auth.selectedProfile.personID);
	const groupSettingGeneralID = useSelector(selectGroupSettingsGeneralID);
	const { upsertOrgSettingsLoading, upsertPersonSettingsLoading } = useSelector((state) => state.Settings);

	// Local state
	const [settings, setSettings] = useState({});
	const [selectedSettingGroup, setSelectedSettingGroup] = useState({});
	const [fieldChange, setFieldChange] = useState(false);
	const [previousSettings, setPreviousSettings] = useState([]);
	const [fileList, setFileList] = useState([]);

	useEffect(() => {
		if (entityType === CONSTANTS_SETTINGS.ORG_SETTING_TYPE && orgSettings.length > 0) {
			setSettings(orgSettings?.filter((a) => a.groupID === settingGroupID));
			setPreviousSettings(orgSettings?.filter((a) => a.groupID === settingGroupID));
			setSelectedSettingGroup(groupSettingsData?.find((a) => a.groupID === settingGroupID));
		} else {
			setSettings(personSettings);
			setPreviousSettings(personSettings);
		}

		return () => {
			setSettings([]);
		};
	}, [settingGroupID, orgSettings, personSettings, groupSettingsData, entityType]);

	const deleteFileFromFirebaseStorage = async (filePath) => {
		await deleteFiles(filePath);
	};

	const handleDiscardChanges = async () => {
		setSettings(previousSettings);
		const changedSettings = _.filter(settings, (current) => {
			const previous = previousSettings.find((prev) => prev.defaultSettingID === current.defaultSettingID);
			return !_.isEqual(current, previous);
		});
		const uploadField = changedSettings.find((setting) => setting.fieldTypeID === settingFieldType.FILEUPLOAD);
		if (uploadField) {
			deleteFileFromFirebaseStorage(uploadField.value);
			setFileList([]);
		}
	};

	const handleSaveChanges = () => {
		const changedSettings = _.filter(settings, (current) => {
			const previous = previousSettings.find((prev) => prev.defaultSettingID === current.defaultSettingID);
			return !_.isEqual(current, previous);
		});

		if (changedSettings.length === 0) return;

		dispatch(
			upsertSettingsByEntityType({
				entityType,
				settings: changedSettings,
				personID
			})
		);
	};

	useLayoutEffect(() => {
		const changeData = _.isEqual(settings, previousSettings);
		setFieldChange(changeData);
	}, [settings, previousSettings]);

	return (
		<Flex vertical flex={1}>
			{entityType === CONSTANTS_SETTINGS.ORG_SETTING_TYPE ? (
				<>
					<RenderHeaderSetting
						title={selectedSettingGroup?.title}
						description={selectedSettingGroup?.description}
						showDiscardButton={!fieldChange}
						showSaveButton={true}
						disableSaveButton={fieldChange}
						onClickDiscard={handleDiscardChanges}
						onClickSave={handleSaveChanges}
						loadingSaveButton={upsertOrgSettingsLoading}
					/>

					{settingGroupID === groupSettingGeneralID && <GeneralSetting />}

					{settings.length > 0 &&
						settings.map((item, index) => {
							return (
								<Row key={index} style={{ marginBottom: marginMD }} gutter={[marginMD]} align='top'>
									<Col xs={24} md={16}>
										<Title level={5} style={{ margin: 0 }}>
											{item.title}
										</Title>
										<Text style={{ color: colorTextLabel }}>{item.description}</Text>
									</Col>
									<Col xs={24} md={8}>
										<div style={{ height: 26 }} /> {/* Placeholder for alignment */}
										<Flex flex={1} justify='end' align='end'>
											<RenderFieldTypeSetting
												setting={item}
												index={index}
												settings={settings}
												setSettings={setSettings}
												setPreviousSettings={setPreviousSettings}
												fileList={fileList}
												setFileList={setFileList}
												handleRemoveUpload={handleDiscardChanges}
											/>
										</Flex>
									</Col>
								</Row>
							);
						})}

					{settingGroupID === groupSettingGeneralID && <DeleteOrganisationSetting />}
				</>
			) : (
				<>
					<RenderHeaderProfileSetting />

					{settings.length > 0 &&
						settings.map((item, index) => {
							return (
								<Row key={index} style={{ marginBottom: marginMD }} gutter={[marginMD]} align={'top'}>
									<Col xs={24} md={16}>
										<Title level={5} style={{ margin: 0 }}>
											{item.title}
										</Title>
										<Text style={{ color: colorTextDescription }}>{item.description}</Text>
									</Col>
									<Col xs={24} md={8}>
										<div style={{ height: 26 }} /> {/* Placeholder for alignment */}
										<Flex flex={1} justify='end' align='end'>
											<RenderFieldTypeSetting
												setting={item}
												index={index}
												settings={settings}
												setSettings={setSettings}
												setPreviousSettings={setPreviousSettings}
												fileList={fileList}
												setFileList={setFileList}
												handleRemoveUpload={handleDiscardChanges}
											/>
										</Flex>
									</Col>
								</Row>
							);
						})}
					<Flex align='center' justify='space-between' style={{ marginBottom: marginMD }}>
						<Flex vertical>
							<Title style={{ margin: 0 }} level={5}>
								Change Password
							</Title>
							<Text style={{ color: colorTextDescription }}>Change password to a new password.</Text>
						</Flex>

						<Flex>
							{[...Array(3)].map((_, i) => (
								<EllipsisHorizontalIcon
									key={i}
									width={32}
									height={32}
									style={{ marginLeft: -paddingXXS }}
								/>
							))}
						</Flex>

						<Button
							style={{ fontWeight: 500 }}
							icon={<EditIcon />}
							onClick={() => setOpenPasswordModal(true)}
						>
							Change
						</Button>
					</Flex>
					<RenderHeaderSetting
						title={null}
						description={null}
						showDiscardButton={!fieldChange}
						showSaveButton={true}
						disableSaveButton={fieldChange}
						onClickDiscard={handleDiscardChanges}
						onClickSave={handleSaveChanges}
						loadingSaveButton={upsertPersonSettingsLoading}
					/>
					<ChangePasswordModal open={openPasswordModal} setOpen={setOpenPasswordModal}>
						<div />
					</ChangePasswordModal>
				</>
			)}
		</Flex>
	);
};
export default RenderContentSetting;
